import { isNil, isNotNilOrEmpty } from "client/Misc/utils";
import { ContactListItem } from "client/Api/Email/contacts";
import { never } from "client_legacy/ndaw/errors";

export function groupAlreadySortedContacts<T = ContactListItem>(
	items: readonly T[],
	itemsGroupBy: ContactGroupingKind,
	contactListItemSelector: (x: T) => ContactListItem = (x) => x as ContactListItem
): readonly IContactGroup<T>[] {
	const groupFactory: IContactGroupFactory<T> = getGroupFactory(itemsGroupBy, contactListItemSelector);
	const result: ContactGroup<T>[] = [];
	let currentGroup: ContactGroup<T> | undefined;
	for (const item of items) {
		if (isNil(currentGroup) || !currentGroup.tryAddItem(item)){
			const newGroup: ContactGroup<T> = groupFactory.createGroup(item);
			currentGroup = newGroup;
			result.push(newGroup);
		}
	}

	return result;
}

export interface IContactGroup<T> {
	readonly key: string;
	readonly title: string;
	readonly items: readonly T[];
}

export enum ContactGroupingKind {
	FirstLetterFirstName = 0,
	FirstLetterLastName = 1,
	JobTitle = 2,
	Department = 3,
	Company = 4,
	FirstLetterDisplayName = 5,
	OfficeLocation = 6
};

class ContactGroup<T> implements IContactGroup<T> {
	private readonly _items: T[];
	private readonly _getTitle: (item: ContactListItem) => string;

	public readonly key: string;
	public readonly title: string;
	public contactListItemSelector: (x: T) => ContactListItem;

	public get items(): readonly T[] {
		return this._items;
	}

	constructor(
		item: T,
		getTitle: (item: ContactListItem) => string,
		contactListItemSelector: (x: T) => ContactListItem
	) {
		const contactListItem: ContactListItem = contactListItemSelector(item);

		this.contactListItemSelector = contactListItemSelector;
		this.key = contactListItem.id;
		this.title = getTitle(contactListItem);
		this._items = [item];
		this._getTitle = getTitle;
	}

	public tryAddItem(item: T): boolean {
		if (this.doesMatch(item)) {
			this._items.push(item);
			return true;
		}

		return false;
	}

	private doesMatch(item: T): boolean {
		const contactListItem: ContactListItem = this.contactListItemSelector(item);
		const itemTitle: string = this._getTitle(contactListItem);
		return this.title.toUpperCase() === itemTitle.toUpperCase();
	}
}

interface IContactGroupFactory<T> {
	createGroup(item: T): ContactGroup<T>;
}

abstract class ContactGroupFactory<T> implements IContactGroupFactory<T> {
	protected contactListItemSelector: (item: T) => ContactListItem;

	constructor(contactListItemSelector: (item: T) => ContactListItem) {
		this.contactListItemSelector = contactListItemSelector;
	}

	public createGroup(item: T): ContactGroup<T> {
		return new ContactGroup<T>(
			item,
			item => this.getTitle(item),
			this.contactListItemSelector
		);
	}

	public abstract getTitle(item: ContactListItem): string;
}

class FirstLetterFirstNameContactGroupFactory<T> extends ContactGroupFactory<T> {
	constructor (contactListItemSelector: (item: T) => ContactListItem) {
		super(contactListItemSelector);
	}

	public getTitle(item: ContactListItem): string {
		return getTitleFromFirstLetter(item.firstName);
	}
}

class FirstLetterLastNameContactGroupFactory<T> extends ContactGroupFactory<T> {
	constructor (contactListItemSelector: (item: T) => ContactListItem) {
		super(contactListItemSelector);
	}

	public getTitle(item: ContactListItem): string {
		return getTitleFromFirstLetter(item.lastName);
	}
}

class FirstLetterDisplayNameContactGroupFactory<T> extends ContactGroupFactory<T> {
	constructor (contactListItemSelector: (item: T) => ContactListItem) {
		super(contactListItemSelector);
	}

	public getTitle(item: ContactListItem): string {
		return getTitleFromFirstLetter(item.displayName);
	}
}

class JobTitleContactGroupFactory<T> extends ContactGroupFactory<T> {
	constructor (contactListItemSelector: (item: T) => ContactListItem) {
		super(contactListItemSelector);
	}

	public getTitle(item: ContactListItem): string {
		if (isNotNilOrEmpty(item.jobTitle)) {
			return item.jobTitle;
		} else {
			return "";
		}
	}
}

class DepartmentContactGroupFactory<T> extends ContactGroupFactory<T> {
	constructor (contactListItemSelector: (item: T) => ContactListItem) {
		super(contactListItemSelector);
	}

	public getTitle(item: ContactListItem): string {
		if (isNotNilOrEmpty(item.department)) {
			return item.department;
		} else {
			return "";
		}
	}
}

class CompanyContactGroupFactory<T> extends ContactGroupFactory<T> {
	constructor (contactListItemSelector: (item: T) => ContactListItem) {
		super(contactListItemSelector);
	}

	public getTitle(item: ContactListItem): string {
		if (isNotNilOrEmpty(item.company)) {
			return item.company;
		} else {
			return "";
		}
	}
}

class OfficeLocationContactGroupFactory<T> extends ContactGroupFactory<T> {
	constructor (contactListItemSelector: (item: T) => ContactListItem) {
		super(contactListItemSelector);
	}

	public getTitle(item: ContactListItem): string {
		if (isNotNilOrEmpty(item.officeLocation)) {
			return item.officeLocation;
		} else {
			return "";
		}
	}
}

function getGroupFactory<T>(kind: ContactGroupingKind, contactListItemSelector: (item: T) => ContactListItem): IContactGroupFactory<T> {
	switch (kind) {
		case ContactGroupingKind.FirstLetterFirstName:
			return new FirstLetterFirstNameContactGroupFactory<T>(contactListItemSelector);
		case ContactGroupingKind.FirstLetterLastName:
			return new FirstLetterLastNameContactGroupFactory<T>(contactListItemSelector);
		case ContactGroupingKind.FirstLetterDisplayName:
			return new FirstLetterDisplayNameContactGroupFactory<T>(contactListItemSelector);
		case ContactGroupingKind.JobTitle:
			return new JobTitleContactGroupFactory<T>(contactListItemSelector);
		case ContactGroupingKind.Department:
			return new DepartmentContactGroupFactory<T>(contactListItemSelector);
		case ContactGroupingKind.Company:
			return new CompanyContactGroupFactory<T>(contactListItemSelector);
		case ContactGroupingKind.OfficeLocation:
			return new OfficeLocationContactGroupFactory<T>(contactListItemSelector);
		default:
			never(kind);
	}
}

function getTitleFromFirstLetter(name: string | null): string {
	if (isNotNilOrEmpty(name)) {
		const firstLetter: string = name[0];
		if (firstLetter.match(/^\d$/)) {
			return "#";
		}
		return firstLetter.toUpperCase();
	} else {
		return "";
	}
}
